import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Order } from '@hero/schema';
import { useDriverApi, useVehicleApi } from '@hero/api-hooks';
import { useOrderActions } from '@hero/hooks-shared';
import { RefreshIcon } from '@hero/web-icons';

import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '../../../ui/Sheet/Sheet';
import { Button } from '../../../ui/Button/Button';
import Spinner from '../../../app/Spinner/Spinner';
import { DriverSummaryTab } from '../../Driver/Drawer/DriverSummaryTab';
import { VehicleSummaryTab } from '../../Vehicle/Drawer/VehicleSummaryTab';
import { OrderDriverPickerDrawer } from './OrderDriverPickerDrawer';

export interface OrderSelectedDriverAndVehicleDrawerProps {
  /** Order whose assigned driver/vehicle we're showing. `null` keeps the drawer mounted but inert. */
  order: Order | null;
  /** Whether the drawer is open. Caller controls visibility. */
  isOpen: boolean;
  /** Close handler — fires on overlay click / esc / explicit cancel. */
  onClose: () => void;
}

/**
 * Read-only "what's assigned to this order" drawer. Shown when the
 * `DriverCell` is clicked on a row that already has a driver. Layers
 * on top of the order drawer the same way the picker does, so the
 * user gets a focused detail surface for driver + vehicle without
 * navigating away.
 *
 * Composition is deliberate: we re-mount `DriverSummaryTab` and
 * `VehicleSummaryTab` (the same panels that power the standalone
 * `DriverDrawer` / `VehicleDrawer`) so legal-doc and image rendering
 * stays consistent everywhere. Both tabs need full entity docs (not
 * the order's snapshot), so we fetch on open via `useDriverApi` /
 * `useVehicleApi` keyed on the snapshot ids — the calls are tiny and
 * already cached when the user hit the drawer from the table.
 *
 * The footer "Change driver" CTA opens `OrderDriverPickerDrawer`,
 * keeping the row-level reassignment flow identical to the empty-
 * driver path. Once the picker fires `onAssigned`, this drawer
 * closes too — both surfaces would otherwise show stale state.
 */
export function OrderSelectedDriverAndVehicleDrawer({
  order,
  isOpen,
  onClose,
}: OrderSelectedDriverAndVehicleDrawerProps) {
  const { t } = useTranslation(['m-order', 'm-driver', 'm-vehicle', 'c-main']);
  const [isPickerOpen, setIsPickerOpen] = useState(false);

  // Locked off when the order is `delivered` / `canceled` so we don't
  // surface a CTA the backend would 400. The hook hides the canonical
  // `inactive` set behind a single boolean so the rule lives in one
  // place. Falls back to `false` while `order` is still `null`.
  const { canAssignDriver } = useOrderActions(order ?? ({ status: {} } as Order));

  const driverId = order?.driver?._id;
  const vehicleId = order?.vehicle?._id;

  const { data: driver, isLoading: driverLoading } = useDriverApi(
    isOpen ? driverId : undefined,
  );
  const { data: vehicle, isLoading: vehicleLoading } = useVehicleApi(
    isOpen ? vehicleId : undefined,
  );

  const handleAssigned = () => {
    setIsPickerOpen(false);
    onClose();
  };

  return (
    <>
      <Sheet open={isOpen} onOpenChange={open => !open && onClose()}>
        <SheetContent
          side="right"
          className="flex flex-col p-0 bg-white w-[520px] sm:max-w-[520px]">
          <SheetHeader className="border-b px-6 py-4">
            <SheetTitle>
              {t('m-order:drawer.driver_vehicle.title', {
                defaultValue: 'Driver & vehicle',
              })}
            </SheetTitle>
            <SheetDescription>
              {t('m-order:drawer.driver_vehicle.description', {
                defaultValue: "Who's moving this load and the vehicle they'll use.",
              })}
            </SheetDescription>
          </SheetHeader>

          <div className="flex-1 overflow-y-auto">
            {driverLoading || vehicleLoading ? (
              <div className="flex items-center justify-center gap-2 py-10 text-sm text-gray-500">
                <Spinner className="w-4 h-4" />
                {t('c-main:async_operations.loading', { defaultValue: 'Loading…' })}
              </div>
            ) : (
              <>
                {driver ? (
                  <DriverSummaryTab driver={driver} />
                ) : order?.driver ? (
                  <div className="p-5">
                    <DriverSnapshotFallback order={order} />
                  </div>
                ) : null}

                {vehicle ? (
                  <VehicleSummaryTab vehicle={vehicle} />
                ) : order?.vehicle ? (
                  <div className="p-5">
                    <VehicleSnapshotFallback order={order} />
                  </div>
                ) : null}
              </>
            )}
          </div>

          <div className="border-t p-4 flex items-center justify-between gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsPickerOpen(true)}
              disabled={!order || !canAssignDriver}
              title={
                !canAssignDriver
                  ? t('m-order:drawer.driver_vehicle.change_driver_locked', {
                      defaultValue: 'Driver cannot be changed on a delivered or canceled order',
                    })
                  : undefined
              }>
              <RefreshIcon className="w-4 h-4 me-1" />
              {t('m-order:drawer.driver_vehicle.change_driver', {
                defaultValue: 'Change driver',
              })}
            </Button>
            <Button variant="ghost" onClick={onClose}>
              {t('c-main:crud_buttons.close', { defaultValue: 'Close' })}
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      <OrderDriverPickerDrawer
        order={order}
        isOpen={isPickerOpen}
        onClose={() => setIsPickerOpen(false)}
        onAssigned={handleAssigned}
      />
    </>
  );
}

/**
 * Thin fallback when the full driver doc 404s but the order still
 * has a snapshot — uncommon (deleted driver) but worth handling so
 * we never blow up. Mirrors the field set we already render in
 * `DriverDetails`.
 */
function DriverSnapshotFallback({ order }: { order: Order }) {
  const { t } = useTranslation(['m-driver']);
  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50/40 p-4">
      <div className="text-sm font-medium text-gray-900 mb-1">
        {order.driver?.name || t('drawer.no_driver_assigned')}
      </div>
      <div className="text-xs text-gray-600">
        <span dir="ltr">{order.driver?.phone || ''}</span>
      </div>
    </div>
  );
}

function VehicleSnapshotFallback({ order }: { order: Order }) {
  const { t } = useTranslation(['m-vehicle']);
  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50/40 p-4">
      <div className="text-sm font-medium text-gray-900 mb-1">
        {order.vehicle?.registration?.plate || t('drawer.no_vehicle_assigned')}
      </div>
      <div className="text-xs text-gray-600">
        {order.vehicle?.classification?.type ?? ''}
      </div>
    </div>
  );
}
